import { isReservationCancelled } from '../../../../utils/reservationStatus';
import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock3 } from 'lucide-react';
import { Cita } from '../../../../services/citasService.ts';

interface Props {
    reservations: Cita[];
    initialMonth?: Date;
    onOpenReservation: (reservationId: number) => void;
    compact?: boolean;
}

const monthTitle = (date: Date) => date.toLocaleDateString('es-BO', { month: 'long', year: 'numeric' });
const dateKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const occursOn = (reservation: Cita, day: Date) => {
    if (!reservation.fecha_inicio) return false;
    const key = dateKey(day);
    const end = reservation.fecha_fin || reservation.fecha_inicio;
    if (key < reservation.fecha_inicio || key > end) return false;
    return !(reservation.dias_habiles && (day.getDay() === 0 || day.getDay() === 6));
};

const ClientReservationsCalendar: React.FC<Props> = ({ reservations, initialMonth = new Date(), onOpenReservation, compact = false }) => {
    const [month, setMonth] = useState(() => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
    const days = useMemo(() => {
        const first = new Date(month.getFullYear(), month.getMonth(), 1);
        const start = new Date(first);
        const mondayOffset = (first.getDay() + 6) % 7;
        start.setDate(first.getDate() - mondayOffset);
        return Array.from({ length: 42 }, (_, index) => {
            const day = new Date(start);
            day.setDate(start.getDate() + index);
            return day;
        });
    }, [month]);

    const moveMonth = (offset: number) => setMonth(current => new Date(current.getFullYear(), current.getMonth() + offset, 1));

    return (
        <section aria-label="Calendario de mis reservas" style={{ background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 24, overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,23,42,.05)' }}>
            <header style={{ padding: compact ? '12px 14px' : '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                <button aria-label="Mes anterior" onClick={() => moveMonth(-1)} style={navButtonStyle}><ChevronLeft size={18} /></button>
                <h2 style={{ margin: 0, textTransform: 'capitalize', color: '#0f172a', fontSize: compact ? '1rem' : '1.1rem', fontWeight: 900 }}>{monthTitle(month)}</h2>
                <button aria-label="Mes siguiente" onClick={() => moveMonth(1)} style={navButtonStyle}><ChevronRight size={18} /></button>
            </header>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', background: '#f8fafc' }}>
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(label => (
                    <div key={label} style={{ padding: compact ? '6px 3px' : '9px 4px', textAlign: 'center', fontSize: '.7rem', fontWeight: 800, color: '#64748b' }}>{label}</div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                {days.map(day => {
                    const inMonth = day.getMonth() === month.getMonth();
                    const events = reservations.filter(item => occursOn(item, day));
                    return (
                        <div key={dateKey(day)} style={{ minHeight: compact ? 58 : 108, padding: compact ? 4 : 7, borderRight: '1px solid #f1f5f9', borderTop: '1px solid #f1f5f9', background: inMonth ? '#fff' : '#f8fafc', opacity: inMonth ? 1 : .55 }}>
                            <div style={{ fontSize: '.72rem', fontWeight: 800, color: '#64748b', marginBottom: 5 }}>{day.getDate()}</div>
                            {events.map(item => (
                                <button
                                    key={item.id}
                                    aria-label={`${item.codigo || `Reserva ${item.id}`} ${item.hora_inicio?.slice(0, 5) || 'sin hora'}`}
                                    onClick={() => onOpenReservation(item.id)}
                                    style={{ width: '100%', textAlign: 'left', border: '1px solid #80d8cc', background: '#ecfdf9', color: '#065f46', borderRadius: 7, padding: compact ? '3px 4px' : '5px 6px', marginBottom: compact ? 2 : 4, cursor: 'pointer', overflow: 'hidden' }}
                                >
                                    <strong style={{ display: 'block', fontSize: '.66rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.codigo || `Reserva ${item.id}`}</strong>
                                    {isReservationCancelled(item) && <span style={{ display: 'block', fontSize: '.65rem', fontWeight: 800, color: '#C1348B' }}>Cancelada</span>}
                                    {!compact && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '.62rem', marginTop: 2 }}><Clock3 size={10} />{item.hora_inicio?.slice(0, 5) || 'Sin hora'}</span>}
                                </button>
                            ))}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const navButtonStyle: React.CSSProperties = {
    width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#007FC8', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};

export default ClientReservationsCalendar;
